"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Cell = exports.Row = exports.TableBody = exports.Column = exports.TableHeader = exports.Table = exports.TableLoadMoreItem = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_1 = __importDefault(require("react"));
var react_aria_components_1 = require("react-aria-components");
var icons_1 = require("../../icons");
var theme_1 = require("../../theme");
var react_aria_components_2 = require("react-aria-components");
Object.defineProperty(exports, "TableLoadMoreItem", { enumerable: true, get: function () { return react_aria_components_2.TableLoadMoreItem; } });
/**
 * A table component that provides a structured way to display data in rows and columns.
 *
 * @example
 * ```tsx
 * <Table>
 *   <TableHeader>
 *     <Column>Name</Column>
 *     <Column>Age</Column>
 *   </TableHeader>
 *   <TableBody>
 *     <Row>
 *       <Cell>John</Cell>
 *       <Cell>30</Cell>
 *     </Row>
 *   </TableBody>
 * </Table>
 * ```
 *
 * @param children - The elements that make up the table. Includes the TableHeader, TableBody, Columns, and Rows
 * @param selectionBehavior - How multiple selection should behave in the collection
 * @param disabledBehavior - Whether disabledKeys applies to all interactions, or only selection
 * @param dragAndDropHooks - The drag and drop hooks returned by useDragAndDrop used to enable drag and drop behavior
 * @param disabledKeys - A list of row keys to disable
 * @param selectionMode - The type of selection that is allowed in the collection
 * @param disallowEmptySelection - Whether the collection allows empty selection
 * @param selectedKeys - The currently selected keys in the collection (controlled)
 * @param defaultSelectedKeys - The initial selected keys in the collection (uncontrolled)
 * @param sortDescriptor - The current sorted column and direction
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param onRowAction - Handler that is called when a user performs an action on the row
 * @param onSelectionChange - Handler that is called when the selection changes
 * @param onSortChange - Handler that is called when the sorted column or direction changes
 * @param onScroll - Handler that is called when a user scrolls
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Table = (0, styled_1.default)(react_aria_components_1.Table)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  padding: 0;\n  border: none;\n  outline: none;\n  border-spacing: 0;\n  min-height: 100px;\n  max-width: 100%;\n  word-break: break-word;\n  forced-color-adjust: none;\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"], ["\n  padding: 0;\n  border: none;\n  outline: none;\n  border-spacing: 0;\n  min-height: 100px;\n  max-width: 100%;\n  word-break: break-word;\n  forced-color-adjust: none;\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"])), theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400, theme_1.theme.space.space2);
/**
 * A table header component that provides a container for column headers.
 *
 * @example
 * ```tsx
 * <TableHeader>
 *   <Column>Name</Column>
 *   <Column>Age</Column>
 * </TableHeader>
 * ```
 *
 * @param children - A list of Column(s) or a function. If the latter, a list of columns must be provided using the columns prop
 * @param columns - A list of table columns
 * @param onHoverStart - Handler that is called when a hover interaction starts
 * @param onHoverEnd - Handler that is called when a hover interaction ends
 * @param onHoverChange - Handler that is called when the hover state changes
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.TableHeader = (0, styled_1.default)(react_aria_components_1.TableHeader)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  background: ", ";\n"], ["\n  background: ", ";\n"])), theme_1.theme.colors.n50);
var StyledColumn = (0, styled_1.default)(react_aria_components_1.Column)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  &[data-focused] {\n    z-index: 20;\n    outline: ", " solid ", ";\n    outline-offset: -4px;\n    border-radius: 11px;\n  }\n"], ["\n  &[data-focused] {\n    z-index: 20;\n    outline: ", " solid ", ";\n    outline-offset: -4px;\n    border-radius: 11px;\n  }\n"])), theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400);
var ColumnGroup = (0, styled_1.default)(react_aria_components_1.Group)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  align-content: center;\n  gap: ", ";\n  outline: none;\n  border: none;\n  padding: ", ";\n  text-align: start;\n  font-weight: ", ";\n  color: ", ";\n"], ["\n  display: flex;\n  align-content: center;\n  gap: ", ";\n  outline: none;\n  border: none;\n  padding: ", ";\n  text-align: start;\n  font-weight: ", ";\n  color: ", ";\n"])), theme_1.theme.space.space4, theme_1.theme.space.space16, theme_1.theme.fontWeights.medium, theme_1.theme.colors.n600);
/**
 * A column component that defines a column in the table.
 *
 * @example
 * ```tsx
 * <Column allowsSorting>Name</Column>
 * ```
 *
 * @param children - The column header content
 * @param allowsSorting - Whether the column can be sorted
 * @param defaultSortDirection - The default sort direction for the column
 * @param onSort - Handler that is called when the column is sorted
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
var Column = function (props) { return (react_1.default.createElement(StyledColumn, __assign({}, props), function (_a) {
    var allowsSorting = _a.allowsSorting, sortDirection = _a.sortDirection;
    return (react_1.default.createElement(ColumnGroup, { role: "presentation" },
        react_1.default.createElement("span", null, props.children),
        allowsSorting && sortDirection && (react_1.default.createElement("span", { "aria-hidden": "true" }, sortDirection === 'ascending' ? (react_1.default.createElement(icons_1.ArrowGroupUpIcon, { size: 16 })) : (react_1.default.createElement(icons_1.ArrowGroupDownIcon, { size: 16 })))),
        allowsSorting && !sortDirection && (react_1.default.createElement("span", { "aria-hidden": "true" },
            react_1.default.createElement(icons_1.ArrowGroupUnsortedIcon, { size: 16 })))));
})); };
exports.Column = Column;
/**
 * A table body component that provides a container for table rows.
 *
 * @example
 * ```tsx
 * <TableBody>
 *   <Row>
 *     <Cell>John</Cell>
 *     <Cell>30</Cell>
 *   </Row>
 * </TableBody>
 * ```
 *
 * @param children - The table rows
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.TableBody = (0, styled_1.default)(react_aria_components_1.TableBody)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  background: ", ";\n"], ["\n  background: ", ";\n"])), theme_1.theme.colors.n50);
/**
 * A row component that represents a single row in the table.
 *
 * @example
 * ```tsx
 * <Row>
 *   <Cell>John</Cell>
 *   <Cell>30</Cell>
 * </Row>
 * ```
 *
 * @param children - The cells in the row
 * @param isDisabled - Whether the row is disabled
 * @param isSelected - Whether the row is selected
 * @param onAction - Handler that is called when the row is selected
 * @param autoFocus - Whether the row should receive focus on render
 * @param onHoverStart - Handler that is called when a hover interaction starts
 * @param onHoverEnd - Handler that is called when a hover interaction ends
 * @param onHoverChange - Handler that is called when the hover state changes
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Row = (0, styled_1.default)(react_aria_components_1.Row)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: -4px;\n    border-radius: 11px;\n  }\n\n  &[data-hovered] {\n    td {\n      background: ", ";\n    }\n  }\n\n  &[data-selected] {\n    td {\n      background: ", ";\n      color: ", ";\n    }\n  }\n\n  td {\n    border-top: ", " solid ", ";\n  }\n\n  & not(:last-of-type) {\n    td {\n      border-bottom: ", " solid ", ";\n    }\n  }\n"], ["\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: -4px;\n    border-radius: 11px;\n  }\n\n  &[data-hovered] {\n    td {\n      background: ", ";\n    }\n  }\n\n  &[data-selected] {\n    td {\n      background: ", ";\n      color: ", ";\n    }\n  }\n\n  td {\n    border-top: ", " solid ", ";\n  }\n\n  & not(:last-of-type) {\n    td {\n      border-bottom: ", " solid ", ";\n    }\n  }\n"])), theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400, theme_1.theme.colors.bgScrimHovered, theme_1.theme.colors.surfacePrimary, theme_1.theme.colors.contentPrimary, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n70, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n70);
/**
 * A cell component that represents a single cell in the table.
 *
 * @example
 * ```tsx
 * <Cell>John</Cell>
 * ```
 *
 * @param children - The content to display in the cell
 * @param isDisabled - Whether the cell is disabled
 * @param autoFocus - Whether the cell should receive focus on render
 * @param onHoverStart - Handler that is called when a hover interaction starts
 * @param onHoverEnd - Handler that is called when a hover interaction ends
 * @param onHoverChange - Handler that is called when the hover state changes
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Cell = (0, styled_1.default)(react_aria_components_1.Cell)(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  padding: ", ";\n  outline: none;\n  background: ", ";\n  &[data-focused] {\n    outline: ", " solid ", ";\n    outline-offset: -4px;\n    border-radius: ", ";\n  }\n"], ["\n  padding: ", ";\n  outline: none;\n  background: ", ";\n  &[data-focused] {\n    outline: ", " solid ", ";\n    outline-offset: -4px;\n    border-radius: ", ";\n  }\n"])), theme_1.theme.space.space16, theme_1.theme.colors.surface200, theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400, theme_1.theme.radii.field);
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
//# sourceMappingURL=Table.js.map